import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit2, Package, AlertCircle } from 'lucide-react';
import { equipmentAPI, equipmentBookingAPI, getImageUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EquipmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManage, user } = useAuth();
  const [item, setItem] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [stockForm, setStockForm] = useState({ total: 0, available: 0 });
  const [updatingStock, setUpdatingStock] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemRes, bookingsRes] = await Promise.all([
          equipmentAPI.getOne(id),
          equipmentBookingAPI.getAll({ equipmentId: id, limit: 10 })
        ]);
        setItem(itemRes.data.data);
        setBookings(bookingsRes.data.data || []);
        setStockForm({ total: itemRes.data.data.stock.total, available: itemRes.data.data.stock.available });
      } catch {
        toast.error('Failed to load equipment');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleStockUpdate = async () => {
    setUpdatingStock(true);
    try {
      const res = await equipmentAPI.updateStock(id, stockForm);
      setItem(res.data.data);
      toast.success('Stock updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Stock update failed');
    } finally {
      setUpdatingStock(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!item) return <div className="empty-state"><h3>Equipment not found</h3></div>;

  const availPct = item.stock.total > 0 ? (item.stock.available / item.stock.total) * 100 : 0;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-icon" onClick={() => navigate('/equipment')}><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{item.name}</h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span className="badge badge-blue">{item.category?.replace('_', ' ')}</span>
              <span className={`badge ${item.stock.available > 0 ? 'badge-green' : 'badge-red'}`}>
                {item.stock.available > 0 ? `${item.stock.available} available` : 'Out of stock'}
              </span>
            </div>
          </div>
        </div>
        {item.stock.available > 0 ? (
          <button className="btn btn-primary" onClick={() => navigate(`/equipment/${id}/book`)}>
            <Package size={16} /> Book Now
          </button>
        ) : canManage && (
          <button className="btn btn-primary" onClick={() => navigate(`/equipment/${id}/edit`)}>
            <Edit2 size={16} /> Edit
          </button>
        )}
      </div>

      <div className="tabs">
        {['overview', 'bookings', 'maintenance'].map(t => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} style={{ textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {item.images?.length > 0 && (
              <div style={{ height: 240, borderRadius: 12, overflow: 'hidden' }}>
                <img src={getImageUrl(item.images[0].url)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div className="card">
              <div className="card-header"><span className="card-title">Details</span></div>
              {item.description && <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 16 }}>{item.description}</p>}
              {item.specifications?.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {item.specifications.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: '#F9FAFB', borderRadius: 6, fontSize: 13 }}>
                      <span style={{ color: '#6B7280' }}>{s.key}</span>
                      <span style={{ fontWeight: 500 }}>{s.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card">
              <div className="card-header"><span className="card-title">Pricing</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  ['Daily Rate', `$${item.pricing?.dailyRate?.toLocaleString()}`],
                  item.pricing?.hourlyRate && ['Hourly Rate', `$${item.pricing.hourlyRate}`],
                  item.pricing?.weeklyRate && ['Weekly Rate', `$${item.pricing.weeklyRate}`],
                  ['Security Deposit', `$${item.pricing?.securityDeposit || 0}`],
                ].filter(Boolean).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <span style={{ color: '#6B7280' }}>{k}</span>
                    <span style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stock management */}
            {canManage && (
              <div className="card">
                <div className="card-header"><span className="card-title">Stock Management</span></div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6B7280', marginBottom: 6 }}>
                    <span>Available: {item.stock.available}/{item.stock.total}</span>
                    <span>{availPct.toFixed(0)}%</span>
                  </div>
                  <div style={{ height: 6, background: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${availPct}%`, height: '100%', background: availPct > 50 ? '#10B981' : availPct > 20 ? '#F59E0B' : '#EF4444', borderRadius: 3 }} />
                  </div>
                </div>
                <div className="form-grid form-grid-2" style={{ gap: 10, marginBottom: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Total</label>
                    <input type="number" className="form-input" min={1} value={stockForm.total} onChange={e => setStockForm(p => ({ ...p, total: parseInt(e.target.value) || 1 }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Available</label>
                    <input type="number" className="form-input" min={0} value={stockForm.available} onChange={e => setStockForm(p => ({ ...p, available: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }} onClick={handleStockUpdate} disabled={updatingStock}>
                  {updatingStock ? 'Updating...' : 'Update Stock'}
                </button>
              </div>
            )}

            <div className="card">
              <div className="card-header"><span className="card-title">Requirements</span></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                {item.requirements?.minimumAge && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#6B7280' }}>Min Age</span><span>{item.requirements.minimumAge}+</span></div>}
                {[['licenseRequired', 'License'], ['depositRequired', 'Deposit'], ['idRequired', 'ID Required']].map(([f, l]) => (
                  <div key={f} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6B7280' }}>{l}</span>
                    <span>{item.requirements?.[f] ? '✅ Yes' : '❌ No'}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'bookings' && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr><th>Booking #</th><th>Guest</th><th>Start</th><th>End</th><th>Qty</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No bookings yet</td></tr>
                ) : bookings.map(b => (
                  <tr key={b._id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{b.bookingNumber}</td>
                    <td>{b.guest?.firstName} {b.guest?.lastName}</td>
                    <td>{format(new Date(b.startDate), 'MMM d, yyyy')}</td>
                    <td>{format(new Date(b.endDate), 'MMM d, yyyy')}</td>
                    <td>{b.quantity}</td>
                    <td style={{ fontWeight: 600 }}>${b.pricing?.total?.toLocaleString()}</td>
                    <td><span className={`badge ${b.status === 'confirmed' ? 'badge-green' : b.status === 'picked_up' ? 'badge-blue' : b.status === 'overdue' ? 'badge-red' : 'badge-gray'}`}>{b.status?.replace('_', ' ')}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'maintenance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(item.maintenanceLogs || []).length === 0 ? (
            <div className="empty-state"><Package size={40} /><h3>No maintenance logs</h3></div>
          ) : item.maintenanceLogs.map((log, i) => (
            <div key={i} className="card card-sm">
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span className="badge badge-yellow">{log.type}</span>
                <span style={{ fontSize: 13, color: '#9CA3AF' }}>{log.date ? format(new Date(log.date), 'MMM d, yyyy') : '—'}</span>
              </div>
              <p style={{ fontSize: 14, color: '#374151' }}>{log.description}</p>
              {log.cost && <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>Cost: ${log.cost}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentDetailPage;
